import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { Alert, Loadable } from '../types';
import { AuthContext } from './AuthContext';
import { DAY } from '../constants/TimeConstants';
import { alertService } from '../api/services/alertService';
import { getConfigVar } from '../config';

interface AlertContextType {
  interval: number;
  setInterval: (interval: number) => void;
  alerts: Loadable<Alert>;
  reloadAlerts: () => Promise<void>;
  resolveAlert: (id: string, action: string) => Promise<void>;
  resolveAlerts: (ids: Array<string>, action: string) => Promise<void>;
}

const initialContext: AlertContextType = {
  interval: DAY,
  setInterval: () => {},
  alerts: { loading: true },
  reloadAlerts: () => Promise.resolve(),
  resolveAlert: () => Promise.resolve(),
  resolveAlerts: () => Promise.resolve(),
};

const AlertContext = createContext<AlertContextType>(initialContext);
const ALERT_REFRESH_TIMEOUT = parseInt(getConfigVar('REACT_APP_ALERT_REFRESH_TIMEOUT', '300000')!);

function AlertProvider({ children }: { children: ReactNode }) {
  const { auth } = useContext(AuthContext);
  const [timer, setTimer] = useState(0);
  const [alerts, setAlerts] = useState<Loadable<Alert>>({ loading: true });
  const [interval, setInterval] = useState(DAY);

  // Periodic refresh: each run schedules the next tick by bumping `timer`.
  // `reloadAlerts` is redefined every render but reads the latest closure,
  // so listing it in deps would only cause spurious re-fires.
  useEffect(() => {
    setTimeout(() => setTimer(timer + 1), ALERT_REFRESH_TIMEOUT);
    if (auth && auth.token) {
      // Wait a short time to ensure a token is properly set in apiClient
      setTimeout(() => {
        reloadAlerts().catch((err) => console.log('Error reloading alerts', err));
      }, 500);
    }
  }, [auth, timer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same rationale as the periodic effect above — refresh when interval changes.
  useEffect(() => {
    if (auth && auth.token) {
      // Wait a short time to ensure a token is properly set in apiClient
      setTimeout(() => {
        reloadAlerts().catch((err) => console.log('Error reloading alerts', err));
      }, 500);
    }
  }, [interval]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadAlerts = async () => {
    if (!auth) return Promise.resolve();
    setAlerts(() => ({ loading: true }));
    try {
      const response = await alertService.getAlerts(interval.toString());
      return setAlerts({ loading: false, data: response.data });
    } catch (err) {
      return setAlerts({ loading: false, error: err as Error });
    }
  };

  const resolveAlert = async (id: string, action: string) => {
    await alertService.performAlertAction(id, action);
    setAlerts({ ...alerts, data: alerts.data?.filter((n) => n.id !== id) });
  };

  const resolveAlerts = async (ids: Array<string>, action: string) => {
    await alertService.performBulkAlertAction(action, ids);
    setAlerts({ ...alerts, data: alerts.data?.filter((n) => !ids.includes(n.id)) });
  };

  return (
    <AlertContext.Provider
      value={{
        interval,
        setInterval,
        alerts,
        reloadAlerts,
        resolveAlert,
        resolveAlerts,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
}

export { AlertContext, AlertProvider };
